import { motion } from "framer-motion";
import { Star, Quote, Instagram } from "lucide-react";
import { MEDIA } from "@/lib/config";

const CASES = [
    {
        img: MEDIA.porsche,
        car: "Porsche 911 Carrera",
        stage: "Stage 1",
        before: "385 PS",
        after: "445 PS",
        gain: "+60 PS",
    },
    {
        img: MEDIA.engine,
        car: "Audi RS3 2.5 TFSI",
        stage: "Stage 2",
        before: "400 PS",
        after: "490 PS",
        gain: "+90 PS",
    },
    {
        img: MEDIA.porscheRear,
        car: "VW Golf GTI",
        stage: "Stage 1 + DSG",
        before: "245 PS",
        after: "300 PS",
        gain: "+55 PS",
    },
];

const TESTIMONIALS = [
    {
        name: "Markus K.",
        car: "BMW 340i",
        text: "Unglaublich, wie schnell die Analyse ging. Foto vom Schein hochgeladen, Sekunden später hatte ich alle Werte. Stage 1 fährt sich wie ein neues Auto.",
    },
    {
        name: "Sandra W.",
        car: "Audi A4 TDI",
        text: "Sehr seriöse Beratung – keine übertriebenen Versprechen, sondern realistische Werte. Genau das wollte ich. Top!",
    },
    {
        name: "Daniel P.",
        car: "VW Golf R",
        text: "Der KI-Chat hat mir alle Fragen zur DSG-Optimierung beantwortet. Termin per WhatsApp gebucht. Perfekter Ablauf.",
    },
];

export const References = () => {
    return (
        <section id="referenzen" className="relative scroll-mt-20 border-t border-border bg-[#0A0A0A] py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl">
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                        Referenzen
                    </span>
                    <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                        Erfolgreiche Projekte
                    </h2>
                    <p className="mt-4 text-base text-[#A1A1AA]">
                        Echte Fahrzeuge, echte Ergebnisse. Eine Auswahl unserer durchgeführten Optimierungen.
                    </p>
                </div>

                <div className="mt-14 grid gap-5 md:grid-cols-3">
                    {CASES.map((c, i) => (
                        <motion.div
                            key={c.car}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            className="card-hover group overflow-hidden rounded-xl border border-border bg-surface"
                        >
                            <div className="relative h-48 overflow-hidden">
                                <img
                                    src={c.img}
                                    alt={c.car}
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                                <span className="absolute left-3 top-3 rounded-full bg-primary px-3 py-1 text-xs font-bold text-white">
                                    {c.stage}
                                </span>
                            </div>
                            <div className="p-5">
                                <h3 className="text-lg font-bold text-white">{c.car}</h3>
                                <div className="mt-3 flex items-center justify-between">
                                    <div className="text-sm text-[#A1A1AA]">
                                        <span className="line-through decoration-[#52525B]">{c.before}</span>
                                        <span className="mx-2 text-white">→</span>
                                        <span className="font-semibold text-white">{c.after}</span>
                                    </div>
                                    <span className="text-sm font-bold text-success">{c.gain}</span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Testimonials */}
                <div className="mt-16 grid gap-5 md:grid-cols-3">
                    {TESTIMONIALS.map((t, i) => (
                        <motion.div
                            key={t.name}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            className="rounded-xl border border-border bg-surface p-6"
                        >
                            <Quote className="h-7 w-7 text-primary/40" />
                            <p className="mt-4 text-sm leading-relaxed text-[#D4D4D8]">{t.text}</p>
                            <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                                <div>
                                    <div className="text-sm font-semibold text-white">{t.name}</div>
                                    <div className="text-xs text-[#A1A1AA]">{t.car}</div>
                                </div>
                                <div className="flex gap-0.5">
                                    {[...Array(5)].map((_, s) => (
                                        <Star key={s} className="h-3.5 w-3.5 fill-[#F59E0B] text-[#F59E0B]" />
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Social feed teaser */}
                <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-xl border border-border bg-surface p-6 sm:flex-row">
                    <div className="flex items-center gap-3">
                        <Instagram className="h-6 w-6 text-primary" />
                        <div>
                            <div className="text-sm font-semibold text-white">Folge uns auf Instagram &amp; TikTok</div>
                            <div className="text-xs text-[#A1A1AA]">Täglich neue Projekte, Vorher/Nachher & Sound</div>
                        </div>
                    </div>
                    <a
                        href="https://instagram.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-md border border-border px-5 py-2.5 text-sm font-medium text-white hover:border-primary/50 transition-colors"
                    >
                        @naperformance
                    </a>
                </div>
            </div>
        </section>
    );
};

export default References;
